/** MV-clock helpers mirroring vp/core/clocks.py (segment maths only; frame maths live in lib/frames.ts). */
import { fmtMs } from "../lib/frames";

export type Seg = { mv0_ms: number; song0_ms: number; dur_ms: number };

export function segmentAtMv(tMvMs: number, segs: readonly Seg[]): number | null {
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i] as Seg;
    if (s.mv0_ms <= tMvMs && tMvMs < s.mv0_ms + s.dur_ms) return i;
  }
  return null;
}

export function mvToSong(tMvMs: number, segs: readonly Seg[]): number | null {
  const i = segmentAtMv(tMvMs, segs);
  if (i === null) return null;
  const s = segs[i] as Seg;
  return s.song0_ms + (tMvMs - s.mv0_ms);
}

/** True when [in, out) is not wholly inside one segment (spans a cutscene, an MV edit or MV-only time). */
export function crossesEdit(inMs: number, outMs: number, segs: readonly Seg[]): boolean {
  if (outMs <= inMs) return true;
  const i = segmentAtMv(inMs, segs);
  if (i === null) return true;
  const s = segs[i] as Seg;
  return outMs > s.mv0_ms + s.dur_ms;
}

/** Clip length rules (the server's `clips.*` settings, served as `Timeline.clip_limits`). */
export type ClipLimits = { min_ms: number; max_ms: number; manual_min_ms: number; manual_max_ms: number };
export const DEFAULT_CLIP_LIMITS: ClipLimits = { min_ms: 10_000, max_ms: 30_000, manual_min_ms: 3_000,
  manual_max_ms: 60_000 };

const secs = (ms: number) => `${Number((ms / 1000).toFixed(3))}`;

/** Length outside the usual range but allowed for a hand-made clip (the server accepts it with a warning). */
export function clipWarnings(inMs: number | null, outMs: number | null,
  opts: { limits?: ClipLimits | null; origin?: "manual" | "suggested" | "agent" } = {}): string[] {
  if (inMs == null || outMs == null || outMs <= inMs || opts.origin !== "manual") return [];
  const lim = opts.limits ?? DEFAULT_CLIP_LIMITS;
  const dur = outMs - inMs;
  if (dur >= lim.manual_min_ms && dur <= lim.manual_max_ms && (dur < lim.min_ms || dur > lim.max_ms)) {
    return [`length ${fmtMs(dur)} is outside the usual ${secs(lim.min_ms)}–${secs(lim.max_ms)} s (allowed for a `
      + "hand-made clip)"];
  }
  return [];
}

/** Client-side preflight for a clip window: human-readable problems (the server stays authoritative). A hand-made
 * (`origin` manual) clip may be manual_min–manual_max long; suggested/agent clips min–max. */
export function clipProblems(inMs: number | null, outMs: number | null, segs: readonly Seg[],
  opts: { limits?: ClipLimits | null; origin?: "manual" | "suggested" | "agent" } = {}): string[] {
  const out: string[] = [];
  if (inMs == null || outMs == null) return ["In and Out must both be set"];
  if (outMs <= inMs) return ["Out must be after In"];
  const dur = outMs - inMs;
  const lim = opts.limits ?? DEFAULT_CLIP_LIMITS;
  const [lo, hi] = opts.origin === "manual" ? [lim.manual_min_ms, lim.manual_max_ms] : [lim.min_ms, lim.max_ms];
  if (dur < lo || dur > hi) out.push(`length ${fmtMs(dur)} is outside ${secs(lo)}–${secs(hi)} s`);
  if (segs.length > 0 && crossesEdit(inMs, outMs, segs)) {
    const i = segmentAtMv(inMs, segs);
    if (i === null) out.push(`In ${fmtMs(inMs)} is outside the aligned music`);
    else {
      const s = segs[i] as Seg;
      out.push(`crosses an MV edit at ${fmtMs(s.mv0_ms + s.dur_ms)}`);
    }
  }
  return out;
}
